import { buildRecipeScaleTargets, RECIPE_SCALE_GROUPS } from './batch.js'

// Shared plating / weighing primitives.
//
// The Weights tab and the Prep tab read and write the SAME `weight_entries`
// store, so they have to agree on three things: which component types get
// weighed, the cell key format, and how "plate now" is computed. Keeping them
// here means the two pages can't silently diverge.

export const MEAL_CATEGORIES = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'chef', 'cheat', 'pasta', 'salad', 'salad_2']
// Everything produced/pulled outside the ten-meal plating line. Wellness shots
// and date balls are base-only dishes in the data model; without listing them
// here they leak into Prep/Weights as toppings and inflate plating to 12 dishes.
export const SIDE_CATEGORIES = [
  'broth', 'broth_2', 'oats', 'chia', 'chia_2',
  'wellness', 'wellness_shots', 'date_balls',
]

// One production sequence shared by Plate, Plate Meals, and the Admin monitor:
// four highest-volume non-pasta meals, pasta fifth, the remaining meals by
// volume, then both salads. Keeping this here prevents the two plating screens
// from drifting into different assembly-line orders.
export function sortPlatingDishes(dishes) {
  const byVolume = (a, b) => (
    (Number(b.meal_count) || 0) - (Number(a.meal_count) || 0) ||
    String(a.name || '').localeCompare(String(b.name || ''))
  )
  const active = (dishes || []).filter((dish) => !SIDE_CATEGORIES.includes(dish.category))
  const salads = active
    .filter((dish) => dish.category === 'salad' || dish.category === 'salad_2')
    .sort(byVolume)
  const pasta = active
    .filter((dish) => dish.category === 'pasta')
    .sort(byVolume)
  const otherMeals = active
    .filter((dish) => dish.category !== 'pasta' && dish.category !== 'salad' && dish.category !== 'salad_2')
    .sort(byVolume)

  return [
    ...otherMeals.slice(0, 4),
    ...pasta,
    ...otherMeals.slice(4),
    ...salads,
  ]
}

// Component types that appear on the plating/weighing tables. 'base' is included so a
// salad's toppings (toasted pumpkin seeds, dried apricots, goat cheese) show up and get
// weighed — before, base was dropped and those ingredients were orphaned. Sauces stay
// out of this list (they're shown by NAME on the Plate-this card, not weighed — see
// PlateThis). Anything NOT covered here is still surfaced by PlateThis's catch-all so a
// meal component can never silently vanish.
export const WEIGHED_TYPES = ['protein', 'vegetable', 'carb', 'base']

export const TYPE_CONFIG = {
  protein:   { label: 'Protein',   color: 'bg-red-50 text-red-700 border-red-200',     dot: 'border-red-300',   order: 1 },
  vegetable: { label: 'Vegetable', color: 'bg-green-50 text-green-700 border-green-200', dot: 'border-green-300', order: 2 },
  carb:      { label: 'Carb',      color: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'border-amber-300', order: 3 },
  base:      { label: 'Topping',   color: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'border-purple-300', order: 4 },
}

// Prep-checklist-only types. A topping (dried apricots, goat cheese, pumpkin
// seeds) has no raw figure to prep to and no cooked weight to record — it either
// got done or it didn't. On the Prep Manager these rows are a checkbox and
// nothing else; on the plate they're simply the prescribed portion.
export const CHECK_ONLY_TYPES = ['base']
export const isCheckOnly = (type) => CHECK_ONLY_TYPES.includes(type)

export const OZ_PER_LB = 16

// ── formatting ────────────────────────────────────────────────────────────────
export const round1 = (n) => Math.round(n * 10) / 10
export const round2 = (n) => Math.round(n * 100) / 100

export function fmtOz(oz) {
  if (oz == null || Number.isNaN(Number(oz))) return '—'
  return `${round1(Number(oz))} oz`
}
export function fmtLbs(oz) {
  if (oz == null || Number.isNaN(Number(oz))) return '—'
  return `${round1(Number(oz) / OZ_PER_LB)} lbs`
}
export function fmtPieces(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return round2(Number(n)) === 1 ? '1 ea' : `${round2(Number(n))} ea`
}

// Potato responsibility follows the preparation, even when an imported menu
// classifies the same component differently from week to week. Roasted potatoes
// belong to prep; mashed/pureed potatoes belong to the chef.
const POTATO_RE = /\b(?:sweet\s+)?potato(?:es)?\b/i
const ROAST_RE = /\broast(?:ed|ing)?\b/i
const MASH_OR_PUREE_RE = /\b(?:mash(?:ed|ing)?|pur(?:e|é)e(?:d|ing)?)\b/i

export function canonicalIngredientType(type, name) {
  const value = String(name || '')
  if (POTATO_RE.test(value) && ROAST_RE.test(value)) return 'vegetable'
  if (POTATO_RE.test(value) && MASH_OR_PUREE_RE.test(value)) return 'carb'
  return type
}

// Kitchen-facing surfaces use the linked recipe as the canonical preparation
// name. `raw_ingredient_name` remains a purchasing label only.
export function componentKitchenName(component) {
  return String(component?.recipe_name || component?.name || '').trim()
}

export function kitchenResponsibility(type, name) {
  const canonicalType = canonicalIngredientType(type, name)
  if (canonicalType === 'protein' || canonicalType === 'carb' || canonicalType === 'sauce') return 'chef'
  if (canonicalType === 'vegetable' || canonicalType === 'base') return 'prep'
  return null
}

// Use the corrected kitchen role for grouping and weighing, while ingKey keeps
// the original persisted type so existing weights/checkoffs survive regrouping.
export const kitchenIngredientType = (component) => canonicalIngredientType(
  component.kitchen_type || component.component_type, component.name,
)

export function orderingRows(ordering) {
  return [
    ...['proteins', 'vegetables', 'carbs', 'sauces', 'bases', 'wellness_shots'].flatMap((key) => ordering?.[key] || []),
    ...Object.values(ordering?.additional || {}).flat(),
  ]
}

export function orderingLookup(ordering) {
  const map = new Map()
  for (const row of orderingRows(ordering)) {
    if (!row.ingredient_name) continue
    map.set(ingKey(row.component_type, row.ingredient_name), row)
    for (const component of row.components || []) {
      map.set(ingKey(component.component_type || row.component_type, component.name), row)
    }
  }
  return map
}

export function menuSauces(menu) {
  const sauces = new Map()
  for (const dish of menu?.dishes || []) {
    for (const component of dish.components || []) {
      if (component.component_type !== 'sauce') continue
      const key = component.recipe_id || component.name.toLowerCase()
      if (!sauces.has(key)) sauces.set(key, { ...component, name: componentKitchenName(component) })
    }
  }
  for (const recipe of menu?.recipe_dependencies || []) {
    if (recipe.category !== 'sauce' || sauces.has(recipe.recipe_id)) continue
    sauces.set(recipe.recipe_id, { ...recipe, component_type: 'sauce' })
  }
  return [...sauces.values()].sort((a, b) => a.name.localeCompare(b.name))
}

// Cell key shared by Prep, Chef Work, Admin, and Weights. Canonicalizing the
// type prevents (for example) Roasted Sweet Potatoes from becoming two rows and
// two saved weights when one menu calls it a carb and another a vegetable.
export const ingKey = (type, name) => `${canonicalIngredientType(type, name)}::${(name || '').toLowerCase().trim()}`

// Menu components are sometimes named for their preparation even though they
// point at one shared recipe: "Corn" and "Fire Charred Sweet Corn", or
// "Cauliflower" and "Roasted Cauliflower".  Those are one cooked batch and
// therefore one weigh-in. Use the stable linked recipe id as the shared identity;
// genuinely different recipes (Zaatar Roasted Chickpeas vs Roasted Chickpeas,
// Rainbow Carrots vs Carrots) remain separate automatically.
//
// The name-based `ingKey` remains the fallback for unlinked components and is
// also retained as `legacyKey` so existing saved weigh-ins continue to display.
export function ingredientGroup(component) {
  const componentName = component?.name || component?.ingredient_name || ''
  const recipeName = component?.recipe_id && component?.recipe_name
    ? component.recipe_name
    : componentName
  return {
    key: component?.recipe_id ? `recipe::${component.recipe_id}` : ingKey(component?.component_type, recipeName),
    legacyKey: ingKey(component?.component_type, componentName),
    legacyKeys: [...new Set([
      ingKey(component?.component_type, componentName),
      ingKey(component?.component_type, recipeName),
    ])],
    name: recipeName,
  }
}

// Read a grouped cell without orphaning values saved under an older component
// label. A newly entered canonical value always wins. Legacy aliases are only a
// read fallback; the next edit saves to the canonical recipe-backed key.
export function groupedInput(inputs, section, ingredient) {
  const bucket = inputs?.[section] || {}
  const direct = bucket[ingredient?.key]
  if (direct !== undefined && direct !== null && direct !== '') return direct
  for (const alias of ingredient?.legacyKeys || []) {
    if (alias === ingredient?.key) continue
    const value = bucket[alias]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return undefined
}

export function groupedSaveState(stateFor, inputs, section, ingredient) {
  const direct = stateFor(section, ingredient?.key)
  if (direct !== 'none') return direct
  const bucket = inputs?.[section] || {}
  for (const alias of ingredient?.legacyKeys || []) {
    if (alias === ingredient?.key) continue
    const value = bucket[alias]
    if (value === undefined || value === null || value === '') continue
    const state = stateFor(section, alias)
    if (state !== 'none') return state
  }
  return direct
}

// Clearing a consolidated row must also clear any older component-name cells;
// otherwise the legacy fallback would make the old value reappear on reload.
export function setGroupedCell(setCell, section, ingredient, value) {
  setCell(section, ingredient.key, value)
  if (value !== '' && value !== null && value !== undefined) return
  for (const alias of ingredient?.legacyKeys || []) {
    if (alias !== ingredient.key) setCell(section, alias, '')
  }
}

// Collect every alias before resolving units or quantities. A legacy piece-unit
// save on the second label must apply to the whole batch, regardless of dish order.
export function buildIngredientGroups(dishes, inputs = {}) {
  const groups = new Map()
  for (const dish of dishes || []) {
    const plates = Number(dish.meal_count) || 0
    for (const component of dish.components || []) {
      if (!WEIGHED_TYPES.includes(component.component_type)) continue
      const identity = ingredientGroup(component)
      const row = groups.get(identity.key) || {
        ...identity, ingredient: identity.name,
        type: canonicalIngredientType(component.kitchen_type || component.component_type, component.name),
        recipe_id: component.recipe_id || null, recipeId: component.recipe_id || null,
        recipe_name: component.recipe_name || null, legacyKeys: new Set(), members: [],
      }
      identity.legacyKeys.forEach((key) => row.legacyKeys.add(key))
      row.members.push({
        component,
        plates,
        dishId: dish.dish_id ?? dish.id,
        dishName: dish.name,
        dishCategory: dish.category,
      })
      groups.set(row.key, row)
    }
  }
  const aliasOwners = new Map()
  for (const row of groups.values()) {
    for (const alias of row.legacyKeys) {
      const owners = aliasOwners.get(alias) || new Set()
      owners.add(row.key)
      aliasOwners.set(alias, owners)
    }
  }
  for (const row of groups.values()) {
    // An old generic "Carrots" save cannot safely be assigned to two different
    // recipes. Keep only unambiguous fallback keys; require a fresh weigh-in otherwise.
    row.legacyKeys = [...row.legacyKeys].filter((alias) => aliasOwners.get(alias).size === 1).sort()
    row.unit = groupedInput(inputs, 'unit', row) === 'ea' ? 'ea' : 'lbs'
    row.plates = 0
    row.need = 0
    for (const { component, plates } of row.members) {
      row.plates += plates
      const piece = inputs.piece?.[component.id]
      row.need += row.unit === 'ea'
        ? (piece !== undefined && piece !== '' ? Number(piece) || 0 : 1) * plates
        : ((Number(component.portion_oz) || 0) * plates) / OZ_PER_LB
    }
    const raw = groupedInput(inputs, 'onHand', row)
    row.onHand = raw === undefined || raw === '' ? null : Number(raw) || 0
    row.r = row.onHand != null && row.need > 0 ? row.onHand / row.need : null
    row.ratio = row.r
    row.variance = row.onHand == null ? null : row.onHand - row.need
  }
  return groups
}

// Purchase rows may combine different recipes. Prep needs the individual recipe
// requirement, not the combined carrot/corn purchase weight on every prep task.
export function groupedOrderingLookup(ordering) {
  const map = new Map()
  const rows = [
    ...RECIPE_SCALE_GROUPS.flatMap((key) => ordering?.[key] || []),
    ...Object.values(ordering?.additional || {}).flat(),
  ]
  for (const row of rows) {
    if (row.ingredient_name) map.set(ingKey(row.component_type, row.ingredient_name), row)
    for (const component of row.components || []) {
      map.set(ingKey(component.component_type || row.component_type, component.name), row)
    }
  }
  for (const [recipeId, target] of buildRecipeScaleTargets(ordering)) {
    map.set(`recipe::${recipeId}`, {
      recipe_id: target.recipeId, needed_with_buffer: target.lbsNeeded,
      display_unit: 'lbs', unit: 'lbs',
    })
  }
  return map
}

// ── plating uplift cap ────────────────────────────────────────────────────────
// "Plate now" scales the prescribed portion by r = on hand / need. Scaling DOWN
// stays linear and uncapped — if you're short you genuinely have to stretch what
// you cooked across every plate. Scaling UP is capped at +1 oz above the
// prescribed portion: cooking 2× the requirement should not tell the cook to
// plate a double portion. Surplus beyond +1 oz stays surplus and carries as
// inventory.
//
// `ea`-unit items (salmon and anything else weighed by the piece) are EXCLUDED
// from the cap — callers handle those on the untouched piece path.
export const PLATE_UPLIFT_CAP_OZ = 1

export function plateNowOz(portionOz, r) {
  if (r == null || portionOz == null) return null
  return Math.min(portionOz * r, portionOz + PLATE_UPLIFT_CAP_OZ)
}

// ── current-week menu resolution ──────────────────────────────────────────────
// A prep cook must never silently work last week's list because localStorage
// cached it. `week_of` is the Saturday the menu is cooked for, and prep happens
// on the Friday/Saturday of that same week — so "today's menu" is the soonest
// week_of that hasn't passed yet, not the most recently created menu.
//
// Falls back to the newest past menu (nothing upcoming loaded), then to the
// caller's current selection (no week_of data at all).
export function resolveCurrentWeekMenu(menus, fallbackId = null, today = new Date()) {
  const dated = (menus || []).filter((m) => m && m.week_of)
  if (!dated.length) return fallbackId

  // Local calendar date, not UTC — a menu must not flip a day early in the evening.
  const todayStr = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-')

  const weekOf = (m) => String(m.week_of).slice(0, 10)
  const upcoming = dated
    .filter((m) => weekOf(m) >= todayStr)
    .sort((a, b) => weekOf(a).localeCompare(weekOf(b)))
  if (upcoming.length) return upcoming[0].id

  const past = [...dated].sort((a, b) => weekOf(b).localeCompare(weekOf(a)))
  return past[0].id
}
